// EstateManagement.tsx

import React, {useState, useEffect, useCallback} from 'react';
import {
  collection,
  onSnapshot,
  doc,
  deleteDoc,
  Timestamp,
  query,
} from 'firebase/firestore';
import {ref, deleteObject} from 'firebase/storage';
import {db, storage, firebaseAppId} from '../../firebase';

import EstateFormModal from './realestates/EstateFormModal'; 

import {
  AdminContent,
  AdminTitle,
  ActionButton,
  LoadingSpinner,
  EstateTable,
  TableHeader,
  TableRow,
  TableCell,
  FlexContainer,
  StatusBadge,
} from './AdminDashboard.styles';

// --- Type and Constant Definitions (Keep them for the main page to use) ---
interface Estate {
  id: string;
  category: string;
  status: 'available' | 'sold' | 'pending';
  price: string;
  currencyType?: 'UGX' | 'USD' | 'EUR'; 
  name?: string;
  description?: string;
  imageUrls: string[];
  videoUrls: string[]; 
  // --- LOCATION FIELDS MADE MANDATORY ---
  latitude: number; // Removed '?'
  longitude: number; // Removed '?'
  villageName?: string;
  // --- CONTACT FIELDS MADE MANDATORY ---
  telephone: string; // Removed '?'
  whatsapp: string; // Removed '?'
  
  logbookFileUrl?: string | null;
  landTitleFileUrl?: string | null;
  logbookNames?: string;
  nin?: string;
  telephone1?: string;
  telephone2?: string;
  email?: string;
  make?: string;
  model?: string;
  bodyType?: string;
  fuel?: string;
  transmission?: string;
  steering?: string;
  chassisNumber?: string;
  yearManufacture?: number;
  yearRegistration?: number;
  landTitleOwnerNames?: string;
  landSize?: string;
  landTitleOwnerNin?: string;
  landTitleOwnerTelephone?: string;
  buildingType?: string;
  noOfFloors?: number;
  noOfUnits?: number;
  roadStreetName?: string;
  plotNumber?: number;
  unitDescription?: string;
  propertyType?: string;
  selectedCountry?: string;
  selectedRegion?: string;
  selectedDistrict?: string;
  selectedSubcounty?: string;
  selectedParish?: string;
  selectedVillage?: string;
  createdAt: Date | Timestamp;
  updatedAt: Date | Timestamp;
}

const CATEGORIES = [
  'Land',
  'Residential Houses',
  'Equipment',
  'Buildings (Shopping Malls , Arcades, Apartment , Hotels , Restaurants , Schools , Hospitals , Rentals &Leasing)',
  'Cars',
  'General Property Sales',
  'Property Management'
];

const getCollectionName = (category: string) => {
  return category.toLowerCase().replace(/[^a-z0-9]/g, '_').replace(/_{2,}/g, '_');
};

// Function to delete a file from Firebase Storage
const deleteFileFromStorage = async (url: string) => {
  if (!url) return;
  try {
    // Convert download URL to a valid storage path
    const baseUrl = `https://firebasestorage.googleapis.com/v0/b/${storage.app.options.storageBucket}/o/`;
    const path = decodeURIComponent(url.replace(baseUrl, '').split('?')[0]);

    const fileRef = ref(storage, path);
    await deleteObject(fileRef);
    console.log('✅ File deleted successfully:', path);
  } catch (error: any) {
    if (error.code === 'storage/object-not-found') {
      console.warn('⚠️ File already deleted or not found:', url);
    } else {
      console.error('❌ Error deleting file:', error);
      // Re-throw the error to be caught by the calling function's try/catch block
      throw new Error(`Failed to delete file from storage: ${error.message}`);
    }
  }
};

// --------------------------------------------------------------------

const EstateManagement = () => {
  const [estates, setEstates] = useState<Estate[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Modal State
  const [showModal, setShowModal] = useState(false);
  const [currentEstate, setCurrentEstate] = useState<Estate | null>(null);

  // --- Data Fetching Effect ---
  useEffect(() => {
    const unsubscribeFns: (() => void)[] = [];
    setLoading(true);

    const fetchCategory = (category: string) => {
      const collectionName = getCollectionName(category);
      const categoryRef = collection(db, `artifacts/${firebaseAppId}/${collectionName}`);
      const q = query(categoryRef);
      const unsubscribe = onSnapshot(
        q,
        (snapshot) => {
          const categoryEstates: Estate[] = [];
          snapshot.forEach((doc) => {
            const data = doc.data();
            categoryEstates.push({
              id: doc.id,
              // NOTE: If you still get TS errors, ensure data pulled from DB has all mandatory fields
              ...data,
              createdAt: data.createdAt?.toDate() || new Date(),
              updatedAt: data.updatedAt?.toDate() || new Date(),
            } as Estate);
          });
          
          setEstates(prevEstates => [...prevEstates.filter(e => e.category !== category), ...categoryEstates]);
          setLoading(false);
        },
        (err) => {
          console.error(`Error fetching estates for category ${category}:`, err);
          setError('Failed to load estates. Please try again.');
          setLoading(false);
        }
      );
      unsubscribeFns.push(unsubscribe);
    };

    CATEGORIES.forEach(category => fetchCategory(category));

    return () => unsubscribeFns.forEach(unsubscribe => unsubscribe());
  }, []);

  // --- Modal Handlers ---
  const openAddModal = () => {
    setCurrentEstate(null);
    setShowModal(true);
  };

  const openEditModal = (estate: Estate) => {
    setCurrentEstate(estate);
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setCurrentEstate(null);
  };

  // --- Delete Handler (FIXED LOGIC: Added manual state removal for immediate UI update) ---
  const handleDeleteEstate = useCallback(async (estate: Estate) => {
    if (!window.confirm('Are you sure you want to delete this item? This action is permanent and includes all files!')) return;
    setLoading(true);
    setError(null);
    try {
      // 1. Delete all associated files from Firebase Storage
      await Promise.all([
        ...estate.imageUrls.map(url => deleteFileFromStorage(url)),
        ...estate.videoUrls.map(url => deleteFileFromStorage(url)), 
        deleteFileFromStorage(estate.logbookFileUrl || ''),
        deleteFileFromStorage(estate.landTitleFileUrl || ''),
      ]);

      // 2. Delete the document from Firestore
      const estateDocRef = doc(db, `artifacts/${firebaseAppId}/${getCollectionName(estate.category)}`, estate.id);
      await deleteDoc(estateDocRef);

      // 3. OPTIMISTIC/MANUAL UI UPDATE (Since the onSnapshot takes a moment, this provides immediate feedback)
      setEstates(prevEstates => prevEstates.filter(e => e.id !== estate.id)); 

    } catch (err: any) {
      console.error('Error deleting estate:', err);
      // Provide a more detailed error message to the user
      setError(`Failed to delete estate: ${err.message || 'Check console for details (often a Firebase Permission error).'}`);
    } finally {
      setLoading(false);
    }
  }, []);

  return (
    <AdminContent>
      <FlexContainer>
        <AdminTitle>Real Estate Management</AdminTitle>
        <ActionButton onClick={openAddModal} className="approve">
          Add New Item
        </ActionButton>
      </FlexContainer>

      {loading && <LoadingSpinner />}
      {error && <p style={{color: 'red'}}>🚨 {error}</p>}

      {!loading && estates.length === 0 && !error && (
        <p>No items found. Click "Add New Item" to add one.</p>
      )}

      {!loading && estates.length > 0 && (
        <EstateTable>
          <thead>
            <TableRow>
              <TableHeader>Item Name</TableHeader>
              <TableHeader>Location</TableHeader>
              <TableHeader>Category</TableHeader>
              <TableHeader>Price</TableHeader>
              <TableHeader>Status</TableHeader>
              <TableHeader>Actions</TableHeader>
            </TableRow>
          </thead>
          <tbody>
            {estates.map((estate) => (
              <TableRow key={estate.id}>
                <TableCell>{estate.name || 'N/A'}</TableCell>
                <TableCell>
                  {/* The longitude and latitude fields are now mandatory */}
                  {estate.latitude !== 0 || estate.longitude !== 0
                    ? `Lat: ${estate.latitude.toFixed(4)}, Lng: ${estate.longitude.toFixed(4)}`
                    : estate.villageName || 'N/A'}
                </TableCell>
                <TableCell>{estate.category.charAt(0).toUpperCase() + estate.category.slice(1)}</TableCell>
                <TableCell>{estate.price || 'N/A'}</TableCell>
                <TableCell>
                  <StatusBadge status={estate.status}>
                    {estate.status.charAt(0).toUpperCase() + estate.status.slice(1)}
                  </StatusBadge>
                </TableCell>
                <TableCell>
                  <ActionButton onClick={() => openEditModal(estate)} className="edit">
                    Edit
                  </ActionButton>
                  <ActionButton onClick={() => handleDeleteEstate(estate)} className="delete">
                    Delete
                  </ActionButton>
                  </TableCell>
              </TableRow>
            ))}
          </tbody>
        </EstateTable>
      )}

      {showModal && (
        <EstateFormModal
          isEditing={!!currentEstate}
          initialEstate={currentEstate}
          onClose={closeModal}
        />
      )}
    </AdminContent>
  );
};

export default EstateManagement;